import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { Optometrist } from '@joker/contracts'
import { useTRPC } from '../trpc'
import { Panel, Tile, Loading, ErrorNote, ViewTitle, fmtShort, fmtDate } from '../ui'

/**
 * The optometry practices, and the record somebody keeps by hand about each.
 *
 * Only practices marked `is_optometrist` appear. Oryx's trainee-source field —
 * which is what used to fill this screen — mixes real practices with study
 * names and category labels ("Other", "Paying client - B2C"), and listing those
 * as optometrists made the whole table untrustworthy. They still attribute a
 * patient; they just are not partners.
 *
 * Money stays per practice and is never summed across rows: half of these bill
 * in CHF and half in USD.
 */
export function Optometrists({
  onOpenPractice,
  onEditPractice,
  onOpenPerson,
}: {
  onOpenPractice: (id: string) => void
  onEditPractice: (id: string) => void
  /* An enquiry that converted can be linked to a patient record, so the editor
     needs the same way out that the Leads screen gives it. */
  onOpenPerson: (id: string) => void
}) {
  const trpc = useTRPC()
  const qc = useQueryClient()
  const q = useQuery(trpc.optometrists.list.queryOptions())
  const [sort, setSort] = useState<{ col: Col; dir: 'asc' | 'desc' }>({ col: 'default', dir: 'asc' })
  /* Practices we work with, and practices who have asked. Two states of one
     relationship, so they are sheets of one screen rather than separate tabs —
     and neither belongs in the patient lead funnel. */
  const [sheet, setSheet] = useState<'practices' | 'leads'>('practices')
  const leads = useQuery(trpc.prospects.list.queryOptions({}))
  /* The new-practice form asks for the four things a practice cannot be listed
     without — a name, a country, and what the patient pays against what we keep.
     Everything else is filled in on the practice's own page afterwards, because
     a form long enough to hold all of it is a form nobody finishes. */
  const [adding, setAdding] = useState(false)
  const [draft, setDraft] = useState({ name: '', country: 'US', price: '1000', cut: '600' })
  const create = useMutation(
    trpc.optometrists.create.mutationOptions({
      onSuccess: async (made) => {
        setAdding(false)
        setDraft({ name: '', country: 'US', price: '1000', cut: '600' })
        await qc.invalidateQueries()
        /* Straight into the new practice: the next thing anybody wants is its
           address and its optometrist, and both live there. */
        onEditPractice(made.id)
      },
    }),
  )

  /* Switzerland prices in francs, everywhere else in dollars — the same rule the
     practice page applies, kept here so the first save is already right. */
  const draftCurrency = draft.country.trim().toLowerCase().startsWith('sw') ? 'CHF' : 'USD'

  if (q.error) return <ErrorNote error={q.error} />

  const rows = q.data ? sortRows(q.data, sort) : []
  const on = (col: Col) =>
    setSort((s) =>
      s.col === col ? { col, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: defaultDir(col) },
    )
  const th = (col: Col, label: string, right = false) => (
    <th className={`${right ? 'r ' : ''}sortable`} aria-sort={ariaSort(sort, col)}>
      <button type="button" onClick={() => on(col)}>
        {label}
        {sort.col === col && <span aria-hidden="true"> {sort.dir === 'asc' ? '↑' : '↓'}</span>}
      </button>
    </th>
  )

  const optometristLeads = (leads.data ?? []).filter((p) => p.leadKind === 'optometrist')

  const sheetNav = (
    <div className="seg seg-inline" role="tablist" aria-label="Which optometrists">
      <button
        type="button"
        role="tab"
        aria-selected={sheet === 'practices'}
        onClick={() => setSheet('practices')}
      >
        Practices
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={sheet === 'leads'}
        onClick={() => setSheet('leads')}
      >
        Enquiries · {optometristLeads.length}
      </button>
    </div>
  )

  if (sheet === 'leads') {
    return (
      <>
        {sheetNav}
        <ViewTitle suit="card-back">Optometrist enquiries</ViewTitle>
        <p className="view-sub">
          Practices asking about carrying the programme, from the Anaheim conference lists in
          Klaviyo. A different channel from the patient waitlist and counted separately — a practice
          that signs becomes an entry on the Practices sheet, not a patient.
        </p>

        <Panel title="Enquiries" note={`${optometristLeads.length} practices`}>
          {optometristLeads.length === 0 ? (
            <p className="footnote">
              Nothing yet. They arrive from the Anaheim conference lists and land here as soon as
              Klaviyo is pulled.
            </p>
          ) : (
            <div className="scroll-x">
              <table className="data">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Source</th>
                    <th className="r">Asked</th>
                  </tr>
                </thead>
                <tbody>
                  {optometristLeads.map((p) => (
                    <tr
                      key={p.id}
                      tabIndex={0}
                      onClick={() => onOpenPerson(p.id)}
                      onKeyDown={(e) => e.key === 'Enter' && onOpenPerson(p.id)}
                    >
                      <td className="num">{p.code ?? '—'}</td>
                      <td>
                        <strong>{p.name ?? <span className="muted">Unnamed</span>}</strong>
                      </td>
                      <td>{p.email ?? <span className="muted">—</span>}</td>
                      <td className="num">{p.phone ?? <span className="muted">—</span>}</td>
                      <td>{p.source ?? <span className="muted">—</span>}</td>
                      <td className="r num">{fmtDate(p.joinedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="footnote">
            Select one to open it, edit it, or mark it a test. Kept out of the Leads screen
            entirely rather than counted and hidden: a practice enquiry converts differently and is
            worth a different amount, so mixing the two would make both funnels wrong.
          </p>
        </Panel>
      </>
    )
  }

  return (
    <>
      {sheetNav}
      <ViewTitle suit="card-back">Optometrists</ViewTitle>
      <p className="view-sub">
        The practices patients are enrolled through. Oryx records the enrolling optometrist on only
        a handful of accounts, so these rosters are kept here by hand until the full database lands.
        Press <b>Edit</b> to open a practice and set what a patient pays and our cut; press the row
        to see its patients. The table opens grouped by country — US first, then Switzerland, each
        A&ndash;Z by name — and any column header re-sorts it.
      </p>

      {/* The button that opens the form is replaced by the form, rather than
          sitting above it turned into a Cancel — an action that undoes what is
          on screen belongs beside the action that commits it, not overhead. */}
      {!adding && (
        <div className="toolbar">
          <button type="button" className="btn" onClick={() => setAdding(true)}>
            New practice
          </button>
        </div>
      )}

      {adding && (
        <Panel title="New practice" note="the rest can be filled in afterwards">
          <form
            className="pform"
            onSubmit={(e) => {
              e.preventDefault()
              const price = Number(draft.price)
              const cut = Number(draft.cut)
              create.mutate({
                name: draft.name.trim(),
                patch: {
                  practiceName: null,
                  locationName: null,
                  website: null,
                  optometristName: null,
                  optometristIsDoctor: false,
                  optometristEmail: null,
                  optometristPhone: null,
                  contactName: null,
                  contactEmail: null,
                  contactPhone: null,
                  country: draft.country.trim() || null,
                  address: null,
                  city: null,
                  postalCode: null,
                  state: null,
                  currency: draftCurrency,
                  patientPriceCents: Number.isFinite(price) ? Math.round(price * 100) : null,
                  ourCutCents: Number.isFinite(cut) ? Math.round(cut * 100) : null,
                },
              })
            }}
          >
            <fieldset>
              <legend>The four things a listing needs</legend>
              <label className="pfield">
                <span>Name</span>
                <input
                  value={draft.name}
                  required
                  autoFocus
                  placeholder="East Cobb"
                  onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                />
              </label>
              <label className="pfield">
                <span>Country</span>
                <input
                  value={draft.country}
                  placeholder="US"
                  onChange={(e) => setDraft((d) => ({ ...d, country: e.target.value }))}
                />
              </label>
              <label className="pfield">
                <span>Patient pays ({draftCurrency})</span>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={draft.price}
                  onChange={(e) => setDraft((d) => ({ ...d, price: e.target.value }))}
                />
              </label>
              <label className="pfield">
                <span>Our cut ({draftCurrency})</span>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={draft.cut}
                  onChange={(e) => setDraft((d) => ({ ...d, cut: e.target.value }))}
                />
              </label>
            </fieldset>
            <div className="pform-actions">
              <button type="submit" className="btn primary" disabled={create.isPending || !draft.name.trim()}>
                {create.isPending ? 'Creating…' : 'Create practice'}
              </button>
              <button
                type="button"
                className="btn"
                disabled={create.isPending}
                onClick={() => {
                  setAdding(false)
                  setDraft({ name: '', country: 'US', price: '1000', cut: '600' })
                }}
              >
                Cancel
              </button>
              {create.error && <span className="warn-text">{create.error.message}</span>}
            </div>
          </form>
          <p className="footnote">
            A practice of this name already in Joker is listed rather than duplicated — the Oryx
            import creates one for every name in the dashboard&rsquo;s optometrist column, and most
            of them are waiting here unconfirmed. Saving picks that one up and confirms it, which is
            what you want when the dashboard already knew the name.
          </p>
        </Panel>
      )}

      {q.data && (
        <div className="tiles">
          <Tile
            k="Revenue, all practices"
            v={money(rows.reduce((n, o) => n + o.revenueUsdCents, 0), 'USD')}
            d="in USD, net of VAT, each at the rate on their day"
          />
          <Tile k="Practices" v={rows.length} d="confirmed" />
          <Tile
            k="Paying patients"
            v={rows.reduce((n, o) => n + o.paying, 0)}
            d={`of ${rows.reduce((n, o) => n + o.people, 0)} enrolled`}
          />
        </div>
      )}

      <Panel>
        {!q.data ? (
          <Loading what="optometrists" />
        ) : (
          <>
            <div className="scroll-x">
              <table className="data">
                <thead>
                  <tr>
                    {th('practice', 'Practice')}
                    {th('where', 'Where')}
                    {th('people', 'Patients', true)}
                    {th('paying', 'Paying', true)}
                    {th('team', 'Team', true)}
                    {th('unclassified', 'To classify', true)}
                    {th('revenue', 'Our cut', true)}
                    {th('revenueUsd', 'Our cut (USD)', true)}
                    {th('last', 'Last session', true)}
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && (
                    <tr>
                      <td colSpan={10} className="muted empty">
                        No practices confirmed yet.
                      </td>
                    </tr>
                  )}
                  {rows.map((o) => (
                    <tr
                      key={o.id}
                      tabIndex={0}
                      onClick={() => onOpenPractice(o.id)}
                      onKeyDown={(e) => e.key === 'Enter' && onOpenPractice(o.id)}
                    >
                      <td>
                        {/* The business first, its branch under it — "Classic
                            Vision Care" over "East Cobb". Falls back to Oryx's
                            own name while nobody has filled these in. */}
                        <strong>{heading(o)}</strong>
                        {sub(o) && (
                          <>
                            <br />
                            <span className="muted sub-line">{sub(o)}</span>
                          </>
                        )}
                      </td>
                      <td>{where(o) || <span className="muted">—</span>}</td>
                      <td className="r num">{o.people}</td>
                      <td className="r num">{o.paying}</td>
                      <td className="r num">{o.team}</td>
                      <td className="r num">
                        {o.unclassified > 0 ? <b className="warn-text">{o.unclassified}</b> : '—'}
                      </td>
                      <td className="r num">{money(o.revenueCents, o.currency)}</td>
                      <td className="r num">{money(o.revenueUsdCents, 'USD')}</td>
                      <td className="r num">{fmtShort(o.lastSessionAt)}</td>
                      <td className="r">
                        <button
                          type="button"
                          className="btn ghost sm"
                          onClick={(e) => {
                            e.stopPropagation()
                            onEditPractice(o.id)
                          }}
                        >
                          View / Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="footnote">
              <strong>Our cut</strong> is paying patients, less any refunded, times this practice&rsquo;s own rate, in its
              own currency — the Swiss practices bill in CHF, so the two cannot be added.{' '}
              <strong>Our cut (USD)</strong> is the figure that can: every patient converted at the
              rate on the day they were counted as paying, which is why it is summed from the
              patients rather than multiplied out here. <strong>To classify</strong> counts the patients on
              a practice&rsquo;s roster nobody has yet said are a paying patient, a team member or a
              store demo. It is a to-do, not a fact about the practice: until somebody says, those
              people are not in the Paying column and so contribute nothing to our cut. Set it on the
              practice&rsquo;s own page, one row at a time.
            </p>
          </>
        )}
      </Panel>

    </>
  )
}

type Col =
  | 'default'
  | 'practice'
  | 'where'
  | 'people'
  | 'paying'
  | 'team'
  | 'unclassified'
  | 'revenue'
  | 'revenueUsd'
  | 'last'

/* Counts and money read most usefully largest-first; names read A-Z. */
const defaultDir = (col: Col): 'asc' | 'desc' =>
  col === 'practice' || col === 'where' ? 'asc' : 'desc'

const ariaSort = (s: { col: Col; dir: 'asc' | 'desc' }, col: Col) =>
  s.col === col ? (s.dir === 'asc' ? 'ascending' : 'descending') : 'none'

/**
 * Country first, US before Switzerland.
 *
 * The two markets are run differently and priced in different currencies, so the
 * useful default is to see one market at a time, each in alphabetical order.
 * Pressing a column header drops the grouping entirely and orders the whole
 * table by that column.
 */
const COUNTRY_ORDER = ['US', 'SWITZERLAND']
const countryRank = (o: Optometrist) => {
  const i = COUNTRY_ORDER.indexOf((o.country ?? '').trim().toUpperCase())
  return i === -1 ? COUNTRY_ORDER.length : i
}

function sortRows(list: Optometrist[], s: { col: Col; dir: 'asc' | 'desc' }): Optometrist[] {
  const rows = [...list]
  if (s.col === 'default') {
    /* Country first, then A-Z by the name on screen — a list you read down
       looking for a practice you already have in mind, rather than a ranking.
       Two branches of one business tie on the name, so the branch breaks it and
       Classic Vision Care reads East Cobb then Kennesaw. */
    return rows.sort(
      (a, b) =>
        countryRank(a) - countryRank(b) ||
        heading(a).localeCompare(heading(b), undefined, { sensitivity: 'base' }) ||
        (sub(a) ?? '').localeCompare(sub(b) ?? '', undefined, { sensitivity: 'base' }),
    )
  }
  const value = (o: Optometrist): string | number => {
    switch (s.col) {
      case 'practice': return heading(o).toLowerCase()
      case 'where': return (sub(o) ?? '').toLowerCase()
      case 'people': return o.people
      case 'paying': return o.paying
      case 'team': return o.team
      case 'unclassified': return o.unclassified
      /* Across currencies this compares numbers, not value — 600 CHF and 600 USD
         tie here. Within one market, which is how the table is usually read, it
         is exactly right. */
      case 'revenue': return o.revenueCents
      case 'revenueUsd': return o.revenueUsdCents
      case 'last': return o.lastSessionAt ?? ''
      default: return 0
    }
  }
  const dir = s.dir === 'asc' ? 1 : -1
  return rows.sort((a, b) => {
    const x = value(a)
    const y = value(b)
    if (typeof x === 'number' && typeof y === 'number') return (x - y) * dir
    return String(x).localeCompare(String(y)) * dir
  })
}

/* The business, or Oryx's own name for it until somebody says otherwise. */
const heading = (o: Optometrist) => o.practiceName ?? practiceLead(o)

/* The branch under it. Never repeats the line above: an unnamed single-site
   practice gets one line, not the same words twice. */
const sub = (o: Optometrist) => {
  const name = o.locationName ?? o.name
  return name && name !== heading(o) ? name : null
}

const practiceLead = (o: Optometrist) =>
  o.optometristName ? `${o.optometristIsDoctor ? 'Dr. ' : ''}${o.optometristName}` : o.name

const where = (o: Optometrist) =>
  [o.city, o.country === 'US' ? o.state : null, o.country].filter(Boolean).join(', ')

function money(cents: number, currency: string): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currencyDisplay: 'narrowSymbol',
    currency,
    maximumFractionDigits: 0,
  }).format(cents / 100)
}
